import type { AvailableModel } from "@/api/settings";

/** Family display order: capability tiers first, unknown families after. */
const FAMILY_ORDER = ["fable", "mythos", "opus", "sonnet", "haiku"];

/** Parse a provider model id into its family and numeric version parts.
 *  Handles both Anthropic shapes — family-first ("claude-opus-4-8",
 *  "claude-fable-5") and the legacy version-first ("claude-3-5-sonnet-…") —
 *  plus non-Anthropic ids ("gemini-3-pro-image", "imagen-4.0-…").
 *  Date-like tokens (8 digits) are not version parts. */
export function parseModelId(id: string): { family: string; version: number[] } {
  // Provider-namespaced ids ("opencode/claude-opus-5", "opencode-go/kimi-k3")
  // group by the bare model name, not the namespace.
  const bare = id.toLowerCase().split("/").pop() ?? id.toLowerCase();
  const parts = bare.split(/[-_./]/);
  const family =
    parts.find((p) => FAMILY_ORDER.includes(p)) ??
    (parts[0] === "claude" ? (parts[1] ?? id) : (parts[0] ?? id));
  const version = parts
    .filter((p) => /^\d+$/.test(p) && p.length < 6)
    .map(Number);
  return { family, version };
}

/** Descending version-vector compare; ties keep input (API) order. */
function versionDesc(a: number[], b: number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (b[i] ?? 0) - (a[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/** Stable sort: family capability order, then version descending. */
export function sortModels(models: AvailableModel[]): AvailableModel[] {
  return [...models].sort((x, y) => {
    const px = parseModelId(x.id);
    const py = parseModelId(y.id);
    if (px.family !== py.family) {
      const ix = FAMILY_ORDER.indexOf(px.family);
      const iy = FAMILY_ORDER.indexOf(py.family);
      return (
        (ix === -1 ? 99 : ix) - (iy === -1 ? 99 : iy) ||
        px.family.localeCompare(py.family)
      );
    }
    return versionDesc(px.version, py.version);
  });
}

export interface ModelFamilyGroup {
  /** Capitalized family name, e.g. "Fable". */
  family: string;
  models: AvailableModel[];
}

/** Group models by family (capability order), each sorted newest-first. */
export function groupModelsByFamily(models: AvailableModel[]): ModelFamilyGroup[] {
  const groups: ModelFamilyGroup[] = [];
  for (const m of sortModels(models)) {
    const { family } = parseModelId(m.id);
    const label = family.charAt(0).toUpperCase() + family.slice(1);
    const g = groups.find((x) => x.family === label);
    if (g) {
      g.models.push(m);
    } else {
      groups.push({ family: label, models: [m] });
    }
  }
  return groups;
}
